import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { IconButton } from '@/components/ui/icon-button'
import { useT } from '@/components/visitone/visitone-provider'
import {
  formatLongDate,
  formatMonthHeading,
  shiftDays,
  shiftMonths,
  tenantToday,
  weekdayIndex,
  weekdayNames,
} from '@/lib/format/tenant-date'
import { cn } from '@/lib/utils'

/*
 * Calendar: a popover month grid for choosing one calendar day, following the
 * WAI-ARIA APG date picker dialog, non-modal. Days are ISO calendar dates;
 * `today` is the tenant's today, passed in, never the browser's. The week
 * starts on Monday (TECH-DEBT(VIS-327): en-GB until the tenant locale
 * decides). There is no range limit.
 *
 * Keys: arrows move by day, Home and End to the start and end of the week,
 * Page Up and Page Down by month (with Shift, by year), Enter or Space
 * chooses, Escape closes. Pointer down or focus outside the popover closes it,
 * except on `anchor`, the control that opens it; the caller returns focus
 * there on close. Position it from a `relative` parent.
 */
function Calendar({
  value,
  today,
  anchor,
  onSelect,
  onClose,
  className,
}: {
  /** The chosen day, marked as selected. */
  value?: string
  /** The tenant's today, marked as current. */
  today?: string
  anchor?: React.RefObject<HTMLElement | null>
  onSelect: (isoDate: string) => void
  onClose: () => void
  className?: string
}) {
  const t = useT()
  const [focused, setFocused] = React.useState(() => value ?? today ?? tenantToday(null))
  const moveFocus = React.useRef(true)
  const root = React.useRef<HTMLDivElement>(null)
  const grid = React.useRef<HTMLTableElement>(null)
  const monthId = React.useId()
  const hintId = React.useId()

  React.useEffect(() => {
    if (!moveFocus.current) return
    grid.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus()
  }, [focused])

  React.useEffect(() => {
    const outside = (target: EventTarget | null) =>
      target instanceof Node && !root.current?.contains(target) && !anchor?.current?.contains(target)
    const onPointer = (event: PointerEvent) => {
      if (outside(event.target)) onClose()
    }
    const onFocus = (event: FocusEvent) => {
      if (outside(event.target)) onClose()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('focusin', onFocus)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('focusin', onFocus)
    }
  }, [anchor, onClose])

  const move = (next: string, withFocus: boolean) => {
    moveFocus.current = withFocus
    setFocused(next)
  }

  const start = `${focused.slice(0, 7)}-01`
  const length = Number(shiftDays(shiftMonths(start, 1), -1).slice(8))
  const cells: (string | null)[] = [
    ...Array<null>(weekdayIndex(start)).fill(null),
    ...Array.from({ length }, (_, index) => shiftDays(start, index)),
  ]
  while (cells.length % 7) cells.push(null)
  const weeks = Array.from({ length: cells.length / 7 }, (_, index) => cells.slice(index * 7, index * 7 + 7))

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const day = weekdayIndex(focused)
    const moves: Record<string, () => string> = {
      ArrowLeft: () => shiftDays(focused, -1),
      ArrowRight: () => shiftDays(focused, 1),
      ArrowUp: () => shiftDays(focused, -7),
      ArrowDown: () => shiftDays(focused, 7),
      Home: () => shiftDays(focused, -day),
      End: () => shiftDays(focused, 6 - day),
      PageUp: () => shiftMonths(focused, event.shiftKey ? -12 : -1),
      PageDown: () => shiftMonths(focused, event.shiftKey ? 12 : 1),
    }
    const next = moves[event.key]
    if (!next) return
    event.preventDefault()
    move(next(), true)
  }

  return (
    <div
      ref={root}
      role="dialog"
      aria-modal="false"
      aria-label={t('core.calendar.label')}
      aria-describedby={hintId}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        event.stopPropagation()
        onClose()
      }}
      className={cn(
        'absolute left-0 top-full z-30 mt-2 w-[19.5rem] max-w-[calc(100vw-2rem)] rounded-md border border-line bg-surface p-3 shadow-e2',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <IconButton label={t('core.calendar.previousMonth')} onClick={() => move(shiftMonths(focused, -1), false)}>
          <ChevronLeft />
        </IconButton>
        <div id={monthId} aria-live="polite" className="text-sm font-semibold text-fg-strong">
          {formatMonthHeading(start)}
        </div>
        <IconButton label={t('core.calendar.nextMonth')} onClick={() => move(shiftMonths(focused, 1), false)}>
          <ChevronRight />
        </IconButton>
      </div>
      <table ref={grid} role="grid" aria-labelledby={monthId} className="mt-1 w-full table-fixed border-collapse" onKeyDown={onGridKeyDown}>
        <thead>
          <tr>
            {weekdayNames().map((name) => (
              <th key={name.full} scope="col" abbr={name.full} className="h-8 text-center text-xs font-medium text-fg-muted">
                {name.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.find(Boolean)}>
              {week.map((day, index) =>
                day ? (
                  <td key={day} role="gridcell" aria-selected={day === value} className="p-0.5 text-center">
                    <button
                      type="button"
                      data-date={day}
                      tabIndex={day === focused ? 0 : -1}
                      aria-current={day === today ? 'date' : undefined}
                      aria-label={day === today ? t('core.calendar.today', { date: formatLongDate(day) }) : formatLongDate(day)}
                      onClick={() => onSelect(day)}
                      onFocus={() => {
                        if (day !== focused) move(day, true)
                      }}
                      className={cn(
                        'inline-flex size-10 items-center justify-center rounded-sm text-sm tabular-nums text-fg hover:bg-surface-hover',
                        day === today && 'font-semibold underline decoration-2 underline-offset-4',
                        day === value && 'bg-action text-action-fg hover:bg-action-hover',
                      )}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </td>
                ) : (
                  <td key={`blank-${index}`} />
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <p id={hintId} className="mt-2 text-xs text-fg-muted">
        {t('core.calendar.hint')}
      </p>
    </div>
  )
}

export { Calendar }
